import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { onMainContentChange } from './animations/animation';
import { LoadingService } from './services/loading.service';
import { SidenavService } from './services/sidenav.service';
import { Router, NavigationEnd } from '@angular/router';
import { AuthGuardService } from './services/auth-guard.service';
import { MatDialog } from '@angular/material/dialog';
import { DateAdapter } from '@angular/material/core';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [onMainContentChange],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('leftSidenav') public sidenav!: MatSidenav;

  loader = false;
  onSideNavChange!: boolean;

  private idleTimeout: any;
  private isLoggingOut = false;

  private readonly IDLE_TIME = 30 * 60 * 1000; // 30 minutes

  private readonly PUBLIC_ROUTES = [
    '/login',
    '/forgot-password',
    '/reset-password',
    '/reset-mail-sent',
    '/session-timeout',
    '/access-denied',
    '/service-unavailable',
    '/page-not-found',
  ];

  constructor(
    private sidenavService: SidenavService,
    private loadingService: LoadingService,
    private changeDetectorRef: ChangeDetectorRef,
    private authguardService: AuthGuardService,
    private dialog: MatDialog,
    private router: Router,
    private dateAdapter: DateAdapter<Date>
  ) {
    this.dateAdapter.setLocale('en-GB');
  }

  // ===============================
  // INIT
  // ===============================

  ngOnInit(): void {
    // Router change
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.handleRouteChange();
      }
    });

    // Loader
    this.loadingService.onLoaderChange().subscribe(res => {
      this.loader = res;
      this.changeDetectorRef.detectChanges();
    });

    // Sidenav
    this.sidenavService.sideNavState$
      .subscribe(res => this.onSideNavChange = res);
  }

  ngAfterViewInit(): void {
    this.sidenavService.setSidenav(this.sidenav);
  }

  ngOnDestroy(): void {
    this.clearIdleTimer();
  }

  // ===============================
  // ROUTE HANDLING
  // ===============================

  private handleRouteChange(): void {
    if (this.isOnPublicRoute()) {
      this.clearIdleTimer();
    } else {
      this.startIdleTimer();
    }
  }

  private isOnPublicRoute(): boolean {
    const currentUrl = this.router.url.split('?')[0];

    return this.PUBLIC_ROUTES.some(route =>
      currentUrl.startsWith(route)
    );
  }

  // ===============================
  // IDLE LOGIC
  // ===============================

  private startIdleTimer(): void {
    this.clearIdleTimer();

    this.idleTimeout = setTimeout(() => {
      this.logOut();
    }, this.IDLE_TIME);
  }

  private clearIdleTimer(): void {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
      this.idleTimeout = null;
    }
  }

  // Reset timer on user activity
  @HostListener('window:mousemove')
  @HostListener('window:keydown')
  @HostListener('window:click')
  @HostListener('window:scroll')
  resetTimer(): void {
    if (!this.isOnPublicRoute()) {
      this.startIdleTimer();
    }
  }

  // ===============================
  // LOGOUT
  // ===============================

  private logOut(): void {
    if (this.isLoggingOut) return; // prevent multiple logout

    this.isLoggingOut = true;

    this.dialog.closeAll();
    this.authguardService.signOut();
    this.router.navigate(['/session-timeout']);
  }
}
